// src/features/dashboard/admin/tutor/AddTutoringStudentForm.jsx
import React, { useState } from 'react';
import { SUBJECTS } from '../../../../data/subjects';
import { GRADE_LEVELS } from '../../../../data/gradeLevels';
import { CLASS_STREAMS } from '../../../../data/classStreams';

const AddTutoringStudentForm = ({ onAdd, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    gender: '',
    subject: '',
    gradeLevel: '',
    stream: '',
    googleClassroomLink: '',
    zoomLink: '',
    zoomPassword: '',
    schedule: [{ day: '', startTime: '', duration: '' }],
    termData: [{ term: '', termStart: '', termEnd: '' }], // ✅ Added termData
  });
  const [saving, setSaving] = useState(false);

  // ---- Term handlers ----
  const addTermRow = () => {
    setFormData(prev => ({
      ...prev,
      termData: [...prev.termData, { term: '', termStart: '', termEnd: '' }]
    }));
  };

  const removeTermRow = (index) => {
    if (formData.termData.length <= 1) {
      alert('You need at least one term.');
      return;
    }
    const newTermData = formData.termData.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, termData: newTermData }));
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...formData.termData];
    newTermData[index][field] = value;
    setFormData(prev => ({ ...prev, termData: newTermData }));
  };

  // ---- Schedule handlers ----
  const addSession = () => {
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, { day: '', startTime: '', duration: '' }]
    }));
  };

  const removeSession = (index) => {
    if (formData.schedule.length <= 1) {
      alert('You need at least one session.');
      return;
    }
    const newSchedule = formData.schedule.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, schedule: newSchedule }));
  };

  const updateSession = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData(prev => ({ ...prev, schedule: newSchedule }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validSessions = formData.schedule.filter(s => s.day && s.startTime && s.duration);
    if (!formData.name || !formData.surname || !formData.email || !formData.subject || !formData.gradeLevel) {
      alert('Please fill in all required fields.');
      return;
    }
    if (validSessions.length === 0) {
      alert('Please add at least one valid session.');
      return;
    }

    // ✅ Filter out empty term rows
    const validTerms = formData.termData.filter(t => t.term && t.termStart && t.termEnd);

    // Combine grade level and stream into a single grade string
    const combinedGrade = `${formData.gradeLevel}${formData.stream}`.trim();
    const submission = {
      ...formData,
      grade: combinedGrade,
      termData: validTerms, // ✅ Include termData
    };
    delete submission.gradeLevel;
    delete submission.stream;

    setSaving(true);
    try {
      await onAdd(submission);
      onCancel();
    } catch (err) {
      alert('Failed to add student: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Surname *</label>
          <input
            type="text"
            name="surname"
            value={formData.surname}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium">Email *</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium">Gender</label>
        <select
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
      </div>

      {/* Subject – searchable input with datalist */}
      <div>
        <label className="block text-sm font-medium">Subject *</label>
        <input
          type="text"
          name="subject"
          list="subject-options"
          value={formData.subject}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
          placeholder="Type or select a subject"
          required
        />
        <datalist id="subject-options">
          {SUBJECTS.map(subj => (
            <option key={subj} value={subj} />
          ))}
        </datalist>
      </div>

      {/* Grade Level + Stream */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium">Grade Level *</label>
          <select
            name="gradeLevel"
            value={formData.gradeLevel}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          >
            <option value="">Select Grade</option>
            {GRADE_LEVELS.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
        {/*<div>
          <label className="block text-sm font-medium">Stream</label>
          <select
            name="stream"
            value={formData.stream}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">None</option>
            {CLASS_STREAMS.map(stream => (
              <option key={stream} value={stream}>{stream}</option>
            ))}
          </select>
        </div> */}
      </div>

      <div>
        <label className="block text-sm font-medium">Google Classroom Link</label>
        <input
          type="text"
          name="googleClassroomLink"
          value={formData.googleClassroomLink}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      <div>
        <label className="block text-sm font-medium">Zoom Link</label>
        <input
          type="text"
          name="zoomLink"
          value={formData.zoomLink}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      <div>
        <label className="block text-sm font-medium">Zoom Password</label>
        <input
          type="text"
          name="zoomPassword"
          value={formData.zoomPassword}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {/* ===== TERM DATA SECTION ===== */}
      <div className="border-t pt-3">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium">Term Dates</h4>
          <button
            type="button"
            onClick={addTermRow}
            className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
          >
            + Add Term
          </button>
        </div>
        {formData.termData.map((term, idx) => (
          <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
            <div>
              <label className="block text-xs font-medium">Term Name</label>
              <input
                type="text"
                value={term.term || ''}
                onChange={e => updateTerm(idx, 'term', e.target.value)}
                placeholder="e.g., Term 1"
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium">Start Date</label>
              <input
                type="date"
                value={term.termStart || ''}
                onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="flex items-end gap-1">
              <div className="flex-1">
                <label className="block text-xs font-medium">End Date</label>
                <input
                  type="date"
                  value={term.termEnd || ''}
                  onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              {formData.termData.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTermRow(idx)}
                  className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                  title="Remove term"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        ))}
        {formData.termData.length === 0 && (
          <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
        )}
      </div>

      {/* ===== SCHEDULE SECTION ===== */}
      <div className="border-t pt-3">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium">Custom Schedule</h4>
          <button
            type="button"
            onClick={addSession}
            className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
          >
            + Add Session
          </button>
        </div>
        {formData.schedule.map((session, idx) => (
          <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
            <div>
              <label className="block text-xs font-medium">Day</label>
              <input
                type="text"
                value={session.day || ''}
                onChange={e => updateSession(idx, 'day', e.target.value)}
                placeholder="e.g., Monday"
                className="w-full border rounded px-2 py-1 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium">Start Time</label>
              <input
                type="text"
                value={session.startTime || ''}
                onChange={e => updateSession(idx, 'startTime', e.target.value)}
                placeholder="e.g., 09:00"
                className="w-full border rounded px-2 py-1 text-sm"
                required
              />
            </div>
            <div className="flex items-end gap-1">
              <div className="flex-1">
                <label className="block text-xs font-medium">Duration (min)</label>
                <input
                  type="number"
                  value={session.duration || ''}
                  onChange={e => updateSession(idx, 'duration', e.target.value)}
                  placeholder="60"
                  className="w-full border rounded px-2 py-1 text-sm"
                  required
                />
              </div>
              {formData.schedule.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSession(idx)}
                  className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={saving}
        >
          {saving ? 'Adding...' : 'Add Student'}
        </button>
      </div>
    </form>
  );
};

export default AddTutoringStudentForm;